import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { type KeyboardEvent, useId, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProperties {
  value?: string;
  onValueChange?: (value: string) => void;
  onBlur?: () => void;
  options: ComboboxOption[];
  /** When supplied, options are filtered by the server rather than locally. */
  onSearchChange?: (query: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  "aria-required"?: boolean;
}

/**
 * A searchable single-select dropdown — the "combobox" shape `@credit-acceptance/uikit`'s
 * own `Select`/`AutoComplete` can't fill here (see memory: uikit's `Select` destructures a
 * fixed prop list with no `aria-*` passthrough and renders `role="button"`, not a combobox;
 * `AutoComplete` is antd-backed with the same shape). Built on the Radix `Popover` already in
 * `ui/popover.tsx` instead of pulling in `cmdk` — filtering/keyboard nav are hand-rolled, not a
 * dependency. Visually matches `ui/select.tsx`'s `SelectTrigger`/`SelectContent`/`SelectItem`.
 */
export function Combobox({
  value,
  onValueChange,
  onBlur,
  options,
  onSearchChange,
  placeholder,
  searchPlaceholder = "Search...",
  emptyMessage = "No results",
  disabled,
  id,
  className,
  ...ariaProperties
}: ComboboxProperties) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listboxId = useId();
  const selected = options.find((option) => option.value === value);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (onSearchChange || !normalizedQuery) {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [options, query, onSearchChange]);

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    setQuery("");
    onSearchChange?.("");
    setHighlightedIndex(0);
    if (!nextOpen) {
      onBlur?.();
    }
  }

  function selectOption(option: ComboboxOption) {
    onValueChange?.(option.value);
    handleOpenChange(false);
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      }
      case "Enter": {
        event.preventDefault();
        const option = filteredOptions[highlightedIndex];
        if (option) {
          selectOption(option);
        }
        break;
      }
      case "Escape": {
        handleOpenChange(false);
        break;
      }
      default: {
        break;
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          className={cn(
            "border-input text-foreground data-[placeholder]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 shadow-xs flex h-6 w-full min-w-0 items-center justify-between gap-2 whitespace-nowrap rounded-md border bg-transparent px-2.5 py-0.5 text-xs outline-none transition-[color,box-shadow] focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...ariaProperties}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{selected?.label ?? placeholder}</span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) flex flex-col gap-1 p-1"
        align="start"
        onOpenAutoFocus={(event) => {
          // Default Radix behavior focuses the first focusable element inside
          // (the search input), which is what we want — only guard against it
          // landing on the listbox instead.
          const target = event.target as HTMLElement;
          if (target.getAttribute("role") === "listbox") {
            event.preventDefault();
          }
        }}
      >
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            onSearchChange?.(event.target.value);
            setHighlightedIndex(0);
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder={searchPlaceholder}
          className="h-6"
          role="searchbox"
          aria-controls={listboxId}
          aria-label={searchPlaceholder}
        />
        <ul id={listboxId} role="listbox" className="max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 && (
            <li className="text-muted-foreground px-2 py-1.5 text-xs">{emptyMessage}</li>
          )}
          {filteredOptions.map((option, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard activation lives on the search input above (ArrowUp/Down + Enter via handleSearchKeyDown), not per-option
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onClick={() => selectOption(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs outline-none",
                index === highlightedIndex && "bg-accent"
              )}
            >
              <CheckIcon className={cn("size-3.5 shrink-0", option.value === value ? "opacity-100" : "opacity-0")} />
              <span className="truncate">{option.label}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
